import React, { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Box, Typography, TextField, Stack, CircularProgress, Alert, Chip, Button,
  Table, TableBody, TableCell, TableHead, TableRow, InputAdornment, Tooltip,
  Divider,
} from '@mui/material';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import {
  compareInkCost, getArtworkThumbnailByPath,
  InkComparisonResult, InkComparisonVariant,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';

/**
 * InkComparisonView — "is this job cheaper in ECG or in PMS?"
 *
 * You give it a sheet size, a run length and TWO Cloudflow artwork paths — the
 * ECG build and the spot build of the same item — and it prices both with the
 * house ink-mileage model (coverage × sheet area × g/m² × sheets), per
 * separation, at a $/kg you type in.
 *
 * The interesting part is that the answer is not obvious: an ECG build almost
 * always lays down MORE total ink (a solid spot becomes tints across the seven
 * process colours) yet can still cost less, because those seven are cheap
 * standing inks where a brand spot is a dear bespoke mix. Which way it lands
 * moves with coverage, run length and the two price lists — hence a tool
 * rather than a rule of thumb.
 *
 * INK MILEAGE ONLY. Wash-up/make-ready waste, minimum mix quantities and
 * press-pass counts are not modelled, and all three favour ECG — so a PMS win
 * here is a narrow one, and an ECG win is a floor. That caveat is on the screen,
 * not just in this comment.
 *
 * Paths are committed with the Compare button (so we don't fire Cloudflow at
 * half-typed paths); prices then re-cost automatically as you type, debounced.
 * The verdict is always computed SERVER-side — the client never re-implements
 * the arithmetic, so the number on screen and the number in the API are one.
 */

const DEFAULT_LABELS = ['ECG', 'PMS'];

const money = (v: number | null | undefined) =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const kg = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;

const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

/** Price/override lookup key — must match helpers.ink_costing.colour_key. */
const colourKey = (name: string) => (name || '').trim().toLowerCase();

interface VariantForm {
  label: string;
  path: string;
}

/** The inputs that require a fresh artwork read — committed by the button. */
interface Committed {
  sheets: number;
  width: number;
  height: number;
  mileage: number | null;
  variants: VariantForm[];
}

const STATUS_LABEL: Record<InkComparisonVariant['artwork_status'], string> = {
  resolved: 'Artwork read',
  no_path: 'No path',
  not_found: 'Artwork not found at that path',
  error: 'Cloudflow error',
};

export default function InkComparisonView() {
  const [sheets, setSheets] = useState('50000');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [mileage, setMileage] = useState('');
  const [forms, setForms] = useState<VariantForm[]>([
    { label: DEFAULT_LABELS[0], path: '' },
    { label: DEFAULT_LABELS[1], path: '' },
  ]);
  const [committed, setCommitted] = useState<Committed | null>(null);

  // Prices are keyed by side + colour, NOT colour alone: the same "Black" is a
  // different product in an ECG deck than in a spot job, and may well be a
  // different price. The "match prices" button below is how you say they're
  // the same, explicitly.
  const [prices, setPrices] = useState<Record<string, string>>({});
  const priceKey = (idx: number, name: string) => `${idx}|${colourKey(name)}`;

  // Typing a price shouldn't POST per keystroke. The artwork is DB-cached by
  // then, so a settled refetch is cheap.
  const [debouncedPrices, setDebouncedPrices] = useState(prices);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPrices(prices), 400);
    return () => clearTimeout(t);
  }, [prices]);

  const num = (s: string) => {
    const n = parseFloat(s);
    return isNaN(n) || n <= 0 ? null : n;
  };
  const sheetsNum = num(sheets);
  const widthNum = num(width);
  const heightNum = num(height);
  const mileageNum = mileage.trim() === '' ? null : num(mileage);
  const canCompare = !!sheetsNum && !!widthNum && !!heightNum
    && forms.every((f) => f.path.trim().startsWith('cloudflow://'));

  const request = useMemo(() => {
    if (!committed) return null;
    return {
      sheets: committed.sheets,
      sheet_width_mm: committed.width,
      sheet_height_mm: committed.height,
      ...(committed.mileage ? { mileage: committed.mileage } : {}),
      variants: committed.variants.map((v, idx) => ({
        label: v.label.trim() || DEFAULT_LABELS[idx] || `Variant ${idx + 1}`,
        pdf_path: v.path.trim(),
        prices: Object.entries(debouncedPrices).reduce<Record<string, number>>((acc, [k, val]) => {
          const [side, colour] = k.split('|');
          const parsed = parseFloat(val);
          if (Number.isFinite(parsed) && parsed >= 0 && Number(side) === idx) acc[colour] = parsed;
          return acc;
        }, {}),
      })),
    };
  }, [committed, debouncedPrices]);

  const q = useQuery({
    queryKey: productionKeys.inkComparison(JSON.stringify(request)),
    queryFn: () => compareInkCost(request!),
    enabled: request != null,
    staleTime: 5 * 60 * 1000,
    // Every price edit mints a NEW query key (prices are part of the request),
    // so without this `data` drops to undefined between requests and the whole
    // results block unmounts and remounts — which reads as the screen flashing
    // while you type. keepPreviousData holds the last result on screen until the
    // next one lands; the figures update in place instead of vanishing first.
    placeholderData: keepPreviousData,
  });
  const data = q.data;

  /** Copy every price entered on either side onto the other where it's blank. */
  const matchPrices = () => {
    if (!data) return;
    setPrices((prev) => {
      const next = { ...prev };
      data.variants.forEach((v, idx) => {
        const other = idx === 0 ? 1 : 0;
        v.separations.forEach((row) => {
          const mine = next[priceKey(idx, row.name)];
          const theirs = next[priceKey(other, row.name)];
          if (mine && !theirs) next[priceKey(other, row.name)] = mine;
        });
      });
      return next;
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <CompareArrowsRounded sx={{ color: TOKENS.accent }} />
        <Typography sx={{ fontWeight: 700, fontSize: 20 }}>ECG vs PMS — ink cost</Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2.5, maxWidth: 900 }}>
        The same carton, priced two ways. Give it a sheet size, a run length and the two
        artwork versions, and it reads each one’s per-separation coverage from Cloudflow,
        applies the house mileage model, and multiplies by the $/kg you type in. An ECG
        build normally lays down <em>more</em> ink than a spot build and can still come out
        cheaper — that’s the arithmetic this settles.
      </Typography>

      {/* ---- inputs -------------------------------------------------------- */}
      <Box sx={{ p: 2, mb: 2, borderRadius: '12px', bgcolor: TOKENS.lineSoft }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            label="Sheets" size="small" value={sheets} sx={{ width: 140 }}
            onChange={(e) => setSheets(e.target.value)}
            helperText="Run length"
          />
          <TextField
            label="Sheet width" size="small" value={width} sx={{ width: 140 }}
            onChange={(e) => setWidth(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
          />
          <TextField
            label="Sheet height" size="small" value={height} sx={{ width: 140 }}
            onChange={(e) => setHeight(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
          />
          <TextField
            label="Mileage override" size="small" value={mileage} sx={{ width: 200 }}
            onChange={(e) => setMileage(e.target.value)}
            placeholder="auto"
            InputProps={{ endAdornment: <InputAdornment position="end">g/m²</InputAdornment> }}
            helperText="Blank = 1.2 process/ECG, 1.5 spot"
          />
        </Stack>

        <Stack spacing={1.5}>
          {forms.map((f, idx) => (
            <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
              <TextField
                label="Label" size="small" value={f.label} sx={{ width: 120 }}
                onChange={(e) => setForms((prev) =>
                  prev.map((p, i) => (i === idx ? { ...p, label: e.target.value } : p)))}
              />
              <TextField
                label={`Artwork ${idx + 1} — Cloudflow path`} size="small" fullWidth
                value={f.path}
                onChange={(e) => setForms((prev) =>
                  prev.map((p, i) => (i === idx ? { ...p, path: e.target.value } : p)))}
                placeholder="cloudflow://Production/NESW/NESW44517425/Finals/PDF Output/Hi Res PDF ECG/NESW44517425_ECG.pdf"
                error={f.path.trim() !== '' && !f.path.trim().startsWith('cloudflow://')}
                helperText={f.path.trim() !== '' && !f.path.trim().startsWith('cloudflow://')
                  ? 'Must be a cloudflow:// path'
                  : ' '}
                InputProps={{ sx: { fontFamily: TOKENS.monoFont, fontSize: 12 } }}
              />
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
          <Button
            variant="contained" disabled={!canCompare}
            onClick={() => setCommitted({
              sheets: sheetsNum!, width: widthNum!, height: heightNum!,
              mileage: mileageNum, variants: forms,
            })}
            sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong }, textTransform: 'none', fontWeight: 700 }}
          >
            Compare
          </Button>
          {data && (
            <Button
              size="small" startIcon={<SwapHorizRounded />} onClick={matchPrices}
              sx={{ textTransform: 'none', color: TOKENS.ink2 }}
            >
              Match prices across both sides
            </Button>
          )}
          {/* Fixed-width slot: a spinner that mounts and unmounts inside a Stack
              nudges the buttons sideways on every re-cost. Reserve the space and
              fade it instead. */}
          <Box sx={{
            width: 18, height: 18, display: 'flex', alignItems: 'center',
            opacity: q.isFetching ? 1 : 0, transition: 'opacity .15s',
          }}>
            <CircularProgress size={18} />
          </Box>
        </Stack>
      </Box>

      {q.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t run the comparison: {(q.error as Error)?.message ?? 'unknown error'}
        </Alert>
      )}

      {data && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {data.variants.map((v, idx) => (
              <VariantPanel
                key={idx} variant={v} sheets={data.inputs.sheets}
                priceOf={(name) => prices[priceKey(idx, name)] ?? ''}
                setPrice={(name, value) =>
                  setPrices((prev) => ({ ...prev, [priceKey(idx, name)]: value }))}
              />
            ))}
          </Stack>

          {/* The verdict reads as the CONCLUSION of the two tables above it, not
              a headline over them — you see what each side costs, then what that
              means. */}
          <Box sx={{ mt: 2 }}><Verdict data={data} /></Box>

          {data.comparison.notes.length > 0 && (
            <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2 }}>
              <Stack spacing={0.25}>
                {data.comparison.notes.map((n, i) => (
                  <Typography key={i} sx={{ fontSize: 13 }}>{n}</Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Typography sx={{ fontSize: 12, color: TOKENS.ink3, mt: 2, maxWidth: 900 }}>
            <strong>Ink mileage only.</strong> This does not count wash-up and make-ready
            waste (the seven ECG inks stay in the deck; every spot is washed in and out),
            the minimum mix quantity on a spot (a colour needed at 0.6 kg is still mixed —
            and usually billed — at the tub), or an extra press pass when a build needs more
            units than the press has. All three favour ECG, so treat a PMS win as narrow and
            an ECG win as a floor.
          </Typography>
        </>
      )}
    </Box>
  );
}

/** The headline: who's cheaper, by how much, and per 1,000 sheets. */
function Verdict({ data }: { data: InkComparisonResult }) {
  const { comparison, variants, inputs } = data;
  const [a, b] = variants;

  if (!comparison.comparable) {
    return (
      <Alert severity="warning">
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>No verdict yet</Typography>
        <Typography sx={{ fontSize: 13 }}>
          {variants.some((v) => v.unpriced.length > 0)
            ? 'Every separation on both sides needs a $/kg before the money comparison stands up. '
            : 'One side couldn’t be computed. '}
          {a?.total_demand_kg != null && b?.total_demand_kg != null && (
            <>Ink weight is already known: {a.label} {kg(a.total_demand_kg)} vs {b.label} {kg(b.total_demand_kg)}.</>
          )}
        </Typography>
      </Alert>
    );
  }

  const winner = variants.find((v) => v.label === comparison.cheaper_label);
  const loser = variants.find((v) => v.label !== comparison.cheaper_label);

  return (
    <Box sx={{
      p: 2.5, borderRadius: '12px', bgcolor: TOKENS.panel,
      border: `1px solid ${TOKENS.line}`, boxShadow: TOKENS.shadowSm,
    }}>
      <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>
        Cheaper on ink
      </Typography>
      {comparison.cheaper_label == null ? (
        <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2 }}>
          Line ball — both sides cost {money(a.total_cost)}
        </Typography>
      ) : (
        <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 30, fontWeight: 800, color: TOKENS.accent, lineHeight: 1.1 }}>
            {comparison.cheaper_label}
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: TOKENS.good }}>
            saves {money(comparison.cost_delta)}
          </Typography>
          <Typography sx={{ fontSize: 14, color: TOKENS.ink2 }}>
            ({comparison.cost_delta_pct?.toFixed(1)}% less ink cost over {inputs.sheets.toLocaleString()} sheets)
          </Typography>
        </Stack>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
        {variants.map((v) => (
          <Box key={v.label}>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>{v.label}</Typography>
            <Typography sx={{
              fontSize: 20, fontWeight: 800,
              color: v.label === comparison.cheaper_label ? TOKENS.good : TOKENS.ink,
            }}>
              {money(v.total_cost)}
            </Typography>
            {/* No $/1,000 here — each table's total row now carries it, and this
                sits directly beneath them. */}
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
              {kg(v.total_demand_kg)}
            </Typography>
          </Box>
        ))}
        {comparison.kg_delta != null && (
          <Box>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>Ink weight difference</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
              {comparison.kg_delta > 0 ? '+' : ''}{comparison.kg_delta.toFixed(2)} kg
            </Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
              {loser?.label} vs {winner?.label}, second minus first
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The 1UP as pre-press drew it, above the numbers it produced.
 *
 * This is the cheap check that the two paths are actually the two builds of the
 * SAME carton — the failure mode this screen is most exposed to is comparing an
 * ECG build against some other item's spot build and getting a confident,
 * meaningless answer. The trim-area note in the results catches the gross case;
 * the eye catches the rest.
 *
 * Keyed on the path and cached indefinitely, so it is fetched once per artwork
 * and NOT refetched as prices are typed.
 */
function ArtworkPreview({ path }: { path: string | null }) {
  const q = useQuery({
    queryKey: productionKeys.artworkThumbnailByPath(path ?? '', 300),
    queryFn: () => getArtworkThumbnailByPath(path!, 300),
    enabled: !!path,
    staleTime: Infinity,
    retry: false,
  });

  if (!path) return null;

  return (
    <Box sx={{
      mb: 1.5, height: 180, borderRadius: '8px', overflow: 'hidden',
      border: `1px solid ${TOKENS.line}`, bgcolor: TOKENS.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {q.isLoading && <CircularProgress size={20} />}
      {q.isError && (
        // A missing preview is not a broken comparison — the kg above it are
        // still good — so this stays quiet rather than shouting in red.
        <Typography sx={{ fontSize: 12, color: TOKENS.ink3, px: 2, textAlign: 'center' }}>
          No preview available
        </Typography>
      )}
      {q.data?.data_url && (
        <Box
          component="img" src={q.data.data_url} alt={`${path} preview`}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      )}
    </Box>
  );
}

/** One side: its separations, the kg each needs, and the price you put on it. */
function VariantPanel({ variant, sheets, priceOf, setPrice }: {
  variant: InkComparisonVariant;
  sheets: number;
  priceOf: (name: string) => string;
  setPrice: (name: string, value: string) => void;
}) {
  const ok = variant.artwork_status === 'resolved';

  // The rate behind the run: cost scales linearly with sheets in a mileage-only
  // model, so this is the figure that stays true when the run length changes —
  // and the one to quote when comparing against a different job.
  const per1000 = (cost: number | null) =>
    (cost == null || !sheets ? null : (cost / sheets) * 1000);

  return (
    <Box sx={{
      flex: 1, minWidth: 0, p: 2, borderRadius: '12px',
      bgcolor: TOKENS.panel, border: `1px solid ${TOKENS.line}`,
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{variant.label}</Typography>
        <Chip
          size="small" label={STATUS_LABEL[variant.artwork_status]}
          sx={{
            height: 20, fontSize: 11,
            bgcolor: ok ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)',
            color: ok ? TOKENS.good : TOKENS.bad,
          }}
        />
        {variant.cache_hit && (
          <Tooltip title="Coverage came from the artwork cache, not a fresh Cloudflow read">
            <Chip size="small" label="cached" sx={{ height: 20, fontSize: 11, bgcolor: TOKENS.lineSoft, color: TOKENS.ink3 }} />
          </Tooltip>
        )}
      </Stack>
      <Typography sx={{ fontSize: 11, color: TOKENS.ink3, fontFamily: TOKENS.monoFont, wordBreak: 'break-all', mb: 1.5 }}>
        {variant.pdf_path ?? '—'}
      </Typography>

      <ArtworkPreview path={variant.pdf_path} />


      {variant.cloudflow_error && (
        <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{variant.cloudflow_error}</Alert>
      )}

      {variant.separations.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: TOKENS.ink3 }}>
          No ink separations to price.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 11, color: TOKENS.ink3 }}>Separation</TableCell>
              <TableCell align="right" sx={{ fontSize: 11, color: TOKENS.ink3 }}>Coverage</TableCell>
              <TableCell align="right" sx={{ fontSize: 11, color: TOKENS.ink3 }}>g/m²</TableCell>
              <TableCell align="right" sx={{ fontSize: 11, color: TOKENS.ink3 }}>kg</TableCell>
              <TableCell align="right" sx={{ fontSize: 11, color: TOKENS.ink3, width: 110 }}>$/kg</TableCell>
              <TableCell align="right" sx={{ fontSize: 11, color: TOKENS.ink3 }}>Cost</TableCell>
              <TableCell align="right" sx={{ fontSize: 11, color: TOKENS.ink3 }}>
                <Tooltip title="Cost per 1,000 sheets — the rate, independent of this run length">
                  <span>$/1,000</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {variant.separations.map((row) => (
              <TableRow key={row.name}>
                <TableCell sx={{ fontSize: 13 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{
                      width: 12, height: 12, borderRadius: '3px', flexShrink: 0,
                      bgcolor: row.hex_colour ?? 'transparent',
                      border: `1px solid ${TOKENS.line}`,
                    }} />
                    <span>{row.name}</span>
                  </Stack>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{pct(row.coverage_frac)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: row.mileage_overridden ? TOKENS.accent : TOKENS.ink2 }}>
                  {row.mileage_g_per_m2.toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                  {row.demand_kg == null ? '—' : row.demand_kg.toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small" value={priceOf(row.name)}
                    onChange={(e) => setPrice(row.name, e.target.value)}
                    placeholder="—"
                    inputProps={{ style: { textAlign: 'right', fontSize: 13, padding: '4px 6px' } }}
                    sx={{ width: 90 }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>
                  {money(row.cost)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: TOKENS.ink2 }}>
                  {money(per1000(row.cost))}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 800, fontSize: 13, borderBottom: 'none' }}>
                Total · {variant.separations.length} ink{variant.separations.length === 1 ? '' : 's'}
              </TableCell>
              <TableCell colSpan={2} sx={{ borderBottom: 'none' }} />
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: 13, borderBottom: 'none' }}>
                {variant.total_demand_kg?.toFixed(2) ?? '—'}
              </TableCell>
              <TableCell sx={{ borderBottom: 'none' }} />
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: 14, borderBottom: 'none', color: TOKENS.accent }}>
                {money(variant.total_cost)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: 13, borderBottom: 'none', color: TOKENS.accent }}>
                {money(per1000(variant.total_cost))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      {variant.skipped_separations.length > 0 && (
        <Typography sx={{ fontSize: 11, color: TOKENS.ink3, mt: 1 }}>
          Not ink, excluded: {variant.skipped_separations.map((s) => s.name).join(', ')}
        </Typography>
      )}
      {!variant.fully_priced && variant.separations.length > 0 && (
        <Typography sx={{ fontSize: 12, color: TOKENS.warn, mt: 1 }}>
          {variant.unpriced.length} separation{variant.unpriced.length === 1 ? '' : 's'} still need a price.
        </Typography>
      )}
      <Typography sx={{ fontSize: 11, color: TOKENS.ink3, mt: 1 }}>
        Over {sheets.toLocaleString()} sheets.
      </Typography>
    </Box>
  );
}
